import PropTypes from 'prop-types';
import React from 'react';

import { Tab, Tabs } from '@blueprintjs/core';
import PredefinedCorpus from './predefined/PredefinedCorpus';
import CustomCorpus from './custom/CustomCorpus';
import SavedCorpus from './saved/SavedCorpus';
import { withTranslation } from 'react-i18next';
import { CORPUS_TYPES } from './constants';
import { refreshSignIn } from '../../utils'


/* `Corpus` doesn't show any UI, its role is to:
  * - orchestrate the 4 components allowing the user to choose the corpus he wants
  * - deal with the preparation of the `corpus` object of the search payload
  *   and return it to `Dashboard` (which itself orchestrates the entire search section).
  *   As such, individual corpus type components (e.g. PredefinedCorpus) don't have to
  *   know the expectation of formatting of the API. They just return the information in
  *   a simple format and `Corpus` prepares it for the API.
*/
class Corpus extends React.PureComponent {

  state = {
    selectedTabId:'publicCollection',
    refreshed:0, // this integer is incremented everytime a new custom corpus is added
    allLangs:"",
    tokenCredit:0,
    savedCorpora:[],
    collections:null,
    privateCollections:[]
  };

  componentDidMount() {
    this.fetchUserData();
  }

  handleTabChange = (selectedTabId) => {
    this.setState({selectedTabId:selectedTabId});
 }

  handleSelectPredefinedCorpus = (lang, predefinedCorpusNames,mergeSubCorpora) => {
    const predefinedCorpus = {
      type: CORPUS_TYPES.PREDEFINED,
      language: lang,
      name:predefinedCorpusNames, // it is possible to query more than one corpus at a time
      mergeSubCorpora:mergeSubCorpora // indicates whether subCorpora should be merged in case of multiple corpus selection
    };
    this.props.onCorpusReady(predefinedCorpus);
  }

  handleSelectUserSavedCorpus = (userSavedCorpusName,language,contrastCorpus,savedContrastCorpus) => {
    const userSavedCorpus = {
      type: CORPUS_TYPES.SAVED,
      name:[userSavedCorpusName],
      language:language,
      contrastCorpus:contrastCorpus,
      savedContrastCorpus:savedContrastCorpus,
    };
    this.props.onCorpusReady(userSavedCorpus);
  }

  refreshSavedData=() => {
    return this.fetchUserData();
    //console.log("Refresh !");
  }

  async fetchUserData( ) {
    const endpoint = '/get_archives.ajax.php';

    fetch(
      process.env.REACT_APP_API_BASE + endpoint, {
        credentials: process.env.REACT_APP_CREDENTIALS,
        headers: { 'Content-Type': 'application/json' },
      })
      .then((res) => {
        if (res.status === 201) {
          // raffraichissement automatique du login si la session est tombée
          console.log(`Refreshing signing in after logged out`);
          refreshSignIn();
          return;
        }
        if (res.status !== 200) {
          console.log(`Issue reaching ${endpoint}`);
          return;
        }
        
        res.json().then((data) => {
          const savedCorpora=data.corpus.filter( corpus => corpus.file !== "last_corpus.json");
          savedCorpora.sort( (a,b) => b.date > a.date);
          const privateCollections= savedCorpora.filter( 
            corpus => (typeof corpus.summary.collections[0][0] === "string") &&
                      (corpus.summary.collections[0][0].substring(0,6)==="users/")
            ).map( corpus => corpus.summary.collections[0][0] ).sort();

          this.setState({ 
              privateCollections: Array.from(new Set(privateCollections)).sort(), 
              tokenCredit: data.tokenCredit, 
              savedCorpora: savedCorpora,
              savedResults: data.results,
              savedQueries: data.queries
          });
          return data;
        })
      })
      .catch(() => {
        console.log(`Network error when trying to fetch ${endpoint}`);
      })

  }

  
  render() {
    const {t}=this.props;
 
    const publicCollTab=<PredefinedCorpus
                          handleSelectPredefinedCorpus={this.handleSelectPredefinedCorpus}
                          onDescriptionQuery={this.props.onDescriptionQuery}
                          collections={this.props.collections}
                          allLangs={this.props.allLangs}
                        />
    const privateCollTab=<SavedCorpus
                            onSavedCorpusChosen={this.handleSelectUserSavedCorpus}
                            onDescriptionQuery={this.props.onDescriptionQuery}
                            savedCorpora={this.state.savedCorpora}
                            tokenCredit={this.state.tokenCredit}
                            collections={this.props.collections}
                            refreshSavedData={this.refreshSavedData}
                            csvDelimiter={this.props.importParamValues && this.props.importParamValues.csvDelimiter? this.props.importParamValues.csvDelimiter: ";"}
                          />


    const addCustomCorpusTab=<CustomCorpus
                                corpusReadyCallback={this.props.onCorpusReady}
                                importParamValues={this.props.importParamValues}
                                collections={this.props.collections}
                                privateCollections={this.state.privateCollections}
                                refreshSavedData={this.refreshSavedData}
                              />
                                                     

    return (
      <>
      {/* renderActiveTabPanelOnly not optimal : only way to refresh saved corpus list : todo use state variable to trigger list reloading */}
        <Tabs onChange={this.handleTabChange} selectedTabId={this.state.selectedTabId}>
            <Tab id='publicCollection' title={t('publicCollections')} panel={publicCollTab} />
            <Tab id='privateCollection' title={t('privateCollections')} panel={privateCollTab} />
            <Tab id='addCustomCorpus' title={t('addCustomCorpus')} panel={addCustomCorpusTab} />
        </Tabs>
      </>
    );
  }
}

Corpus.propTypes = {
  onCorpusReady: PropTypes.func,
  onDescriptionQuery: PropTypes.func, // to handle the "__colloc__" and "__voc__" query in predefined or saved corpus (corpus stats)
  loadSavedResults: PropTypes.func, 
  importParamValues: PropTypes.object, // for CustomCorpus component
  collections:PropTypes.object,
  allLangs:PropTypes.array,
  pasteQuery:PropTypes.func
};

export default withTranslation()(Corpus);